import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { submitHostEvent } from "@/lib/api";
import { useNavigate } from "react-router-dom";

const EVENT_TYPES = [
    "Build Night",
    "Hackathon",
    "Workshop",
    "Demo Day",
    "Mixer",
    "Other",
];
const CONNECTIONS = [
    "I build on Emergent",
    "New but excited",
    "I run a community",
    "Other",
];
const VENUE_STATUSES = ["Confirmed", "In progress", "Still looking"];

const STEP_LABELS = ["About you", "Your event", "Review"];

const twoWeeksFrom = (d) => {
    const t = new Date(d);
    t.setDate(t.getDate() + 14);
    return t.toISOString().slice(0, 10);
};

const INITIAL = {
    full_name: "",
    email: "",
    social_url: "",
    organised_before: false,
    organised_details: "",
    emergent_connection: "",
    event_name: "",
    event_type: "",
    event_idea: "",
    event_location: "",
    venue_status: "",
    expected_attendees: "",
    target_date: "",
    consent: false,
};

export default function HostApplyModal({ onClose }) {
    const [step, setStep] = useState(1);
    const [form, setForm] = useState(INITIAL);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const navigate = useNavigate();
    const minDate = useMemo(() => twoWeeksFrom(new Date()), []);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    // Lock page scroll while modal is open
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, []);

    const validateStep1 = () => {
        if (!form.full_name.trim()) return "Please add your full name.";
        if (!/^\S+@\S+\.\S+$/.test(form.email))
            return "Please add a valid email.";
        if (!/^https?:\/\/\S+/i.test(form.social_url.trim()))
            return "Add a LinkedIn or X profile URL.";
        if (form.organised_before && !form.organised_details.trim())
            return "Tell us what you organised & how big.";
        if (!form.emergent_connection)
            return "Pick your connection to Emergent.";
        return null;
    };
    const validateStep2 = () => {
        if (!form.event_name.trim()) return "Give your event a name.";
        if (!form.event_type) return "Pick an event type.";
        if (!form.event_idea.trim())
            return "Describe the idea in a sentence or two.";
        if (!form.event_location.trim())
            return "Add the city & country.";
        if (!form.venue_status) return "Set the venue status.";
        const n = Number(form.expected_attendees);
        if (!n || n < 1) return "Expected attendees must be at least 1.";
        if (!form.target_date) return "Pick a target date.";
        if (form.target_date < minDate)
            return "Target date must be at least 2 weeks out.";
        return null;
    };

    const goNext = () => {
        const err = step === 1 ? validateStep1() : validateStep2();
        if (err) {
            toast(err);
            return;
        }
        setStep((s) => Math.min(3, s + 1));
    };

    const onSubmit = async () => {
        if (!form.consent) {
            toast("Please agree to be contacted.");
            return;
        }
        setBusy(true);
        try {
            const data = await submitHostEvent({
                ...form,
                expected_attendees: Number(form.expected_attendees),
            });
            setResult(data);
        } catch (e) {
            toast(e?.response?.data?.detail || "Submission failed.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="host-apply-modal"
            className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-6 md:py-12 px-4"
            onClick={onClose}
        >
            <div
                className="relative w-full max-w-2xl bg-[#0d0d0d] border border-line rounded-2xl p-6 md:p-10"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <div className="vc-section-label text-[#5fe3c9] mb-1">
                            Host an event
                        </div>
                        <h2
                            className="font-display font-black uppercase tracking-tightest"
                            style={{
                                fontSize: "clamp(1.6rem, 3vw, 2.4rem)",
                                color: "#F7F4DA",
                            }}
                        >
                            {result ? "You're in." : STEP_LABELS[step - 1]}
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="host-modal-close"
                        className="text-ink-soft hover:text-ink text-3xl leading-none"
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>

                {!result && <StepDots step={step} />}

                {result ? (
                    <SuccessState result={result} navigate={navigate} onClose={onClose} />
                ) : (
                    <>
                        {step === 1 && <Step1 form={form} set={set} />}
                        {step === 2 && <Step2 form={form} set={set} minDate={minDate} />}
                        {step === 3 && <Step3 form={form} set={set} />}

                        <div className="flex items-center justify-between mt-8 gap-3">
                            <button
                                type="button"
                                onClick={() =>
                                    step === 1 ? onClose() : setStep((s) => s - 1)
                                }
                                className="vc-btn vc-btn-ghost"
                                data-testid="host-back"
                            >
                                {step === 1 ? "Cancel" : "Back"}
                            </button>
                            {step < 3 ? (
                                <button
                                    type="button"
                                    onClick={goNext}
                                    className="vc-btn vc-btn-accent"
                                    data-testid="host-next"
                                >
                                    Continue →
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={onSubmit}
                                    disabled={busy}
                                    className="vc-btn vc-btn-accent disabled:opacity-60"
                                    data-testid="host-submit"
                                >
                                    {busy ? "Submitting…" : "Submit application"}
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function StepDots({ step }) {
    return (
        <div className="flex items-center gap-2 mb-6" data-testid="host-step-dots">
            {[1, 2, 3].map((n) => (
                <div key={n} className="flex-1 flex items-center gap-2">
                    <div
                        className="w-6 h-6 grid place-items-center rounded-full text-[11px] font-display font-bold border"
                        style={
                            n <= step
                                ? { background: "#5fe3c9", borderColor: "#5fe3c9", color: "#0a0a0a" }
                                : { borderColor: "rgba(255,255,255,0.15)", color: "#787763" }
                        }
                    >
                        {n}
                    </div>
                    <span
                        className={`text-[10px] tracking-[0.22em] uppercase font-display font-bold ${n <= step ? "text-ink" : "text-ink-soft"}`}
                    >
                        {STEP_LABELS[n - 1]}
                    </span>
                    {n < 3 && <div className="flex-1 h-px bg-line" />}
                </div>
            ))}
        </div>
    );
}

// ---------------- Steps ----------------

function Field({ label, required, children, testId }) {
    return (
        <label className="block" data-testid={testId}>
            <span className="block text-[10px] tracking-[0.22em] uppercase text-ink-soft mb-1.5">
                {label}
                {required && <span className="text-[#ff6b6b] ml-1">*</span>}
            </span>
            {children}
        </label>
    );
}

const inputCls =
    "w-full bg-white/[0.04] border border-line focus:border-[#5fe3c9] rounded-lg px-3 py-2 outline-none text-ink placeholder:text-ink-soft/60";

function Step1({ form, set }) {
    return (
        <div className="space-y-4">
            <Field label="Full name" required>
                <input
                    data-testid="host-full-name"
                    value={form.full_name}
                    onChange={(e) => set("full_name", e.target.value)}
                    className={inputCls}
                />
            </Field>
            <Field label="Email" required>
                <input
                    type="email"
                    data-testid="host-email"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    className={inputCls}
                />
            </Field>
            <Field label="LinkedIn or X" required>
                <input
                    data-testid="host-social"
                    value={form.social_url}
                    onChange={(e) => set("social_url", e.target.value)}
                    placeholder="https://…"
                    className={inputCls}
                />
            </Field>
            <div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-ink-soft mb-1.5">
                    Have you organised events before? <span className="text-[#ff6b6b]">*</span>
                </div>
                <div className="flex gap-2">
                    {[
                        { v: true, l: "Yes" },
                        { v: false, l: "No" },
                    ].map((o) => (
                        <button
                            key={o.l}
                            type="button"
                            data-testid={`host-organised-${o.l.toLowerCase()}`}
                            onClick={() => set("organised_before", o.v)}
                            className={`px-4 py-2 rounded-full text-xs uppercase tracking-[0.15em] border transition-colors ${form.organised_before === o.v ? "bg-ink text-background border-ink" : "border-line text-ink-soft hover:text-ink"}`}
                        >
                            {o.l}
                        </button>
                    ))}
                </div>
            </div>
            {form.organised_before && (
                <Field label="What did you run — and how big?" required>
                    <input
                        data-testid="host-organised-details"
                        value={form.organised_details}
                        onChange={(e) => set("organised_details", e.target.value)}
                        placeholder="e.g. Ran a 60-person AI hack in Berlin last May"
                        className={inputCls}
                    />
                </Field>
            )}
            <Field label="Your connection to Emergent" required>
                <select
                    data-testid="host-connection"
                    value={form.emergent_connection}
                    onChange={(e) => set("emergent_connection", e.target.value)}
                    className={inputCls}
                >
                    <option value="">— Pick one —</option>
                    {CONNECTIONS.map((c) => (
                        <option key={c} value={c} className="bg-[#0a0a0a]">
                            {c}
                        </option>
                    ))}
                </select>
            </Field>
        </div>
    );
}

function Step2({ form, set, minDate }) {
    return (
        <div className="space-y-4">
            <Field label="Event name" required>
                <input
                    data-testid="host-event-name"
                    value={form.event_name}
                    onChange={(e) => set("event_name", e.target.value)}
                    placeholder="e.g. Osaka Sushi App Build Night"
                    className={inputCls}
                />
            </Field>
            <Field label="Event type" required>
                <select
                    data-testid="host-event-type"
                    value={form.event_type}
                    onChange={(e) => set("event_type", e.target.value)}
                    className={inputCls}
                >
                    <option value="">— Pick one —</option>
                    {EVENT_TYPES.map((t) => (
                        <option key={t} value={t} className="bg-[#0a0a0a]">
                            {t}
                        </option>
                    ))}
                </select>
            </Field>
            <Field label="The idea — theme & who it's for" required>
                <textarea
                    data-testid="host-event-idea"
                    value={form.event_idea}
                    onChange={(e) => set("event_idea", e.target.value)}
                    rows={3}
                    placeholder="What will people build, and who are you inviting?"
                    className={`${inputCls} resize-none`}
                />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="City & country" required>
                    <input
                        data-testid="host-event-location"
                        value={form.event_location}
                        onChange={(e) => set("event_location", e.target.value)}
                        placeholder="Osaka, Japan"
                        className={inputCls}
                    />
                </Field>
                <Field label="Venue status" required>
                    <select
                        data-testid="host-venue-status"
                        value={form.venue_status}
                        onChange={(e) => set("venue_status", e.target.value)}
                        className={inputCls}
                    >
                        <option value="">— Pick one —</option>
                        {VENUE_STATUSES.map((s) => (
                            <option key={s} value={s} className="bg-[#0a0a0a]">
                                {s}
                            </option>
                        ))}
                    </select>
                </Field>
                <Field label="Expected attendees" required>
                    <input
                        type="number"
                        min={1}
                        max={10000}
                        data-testid="host-attendees"
                        value={form.expected_attendees}
                        onChange={(e) => set("expected_attendees", e.target.value)}
                        placeholder="30+"
                        className={inputCls}
                    />
                </Field>
                <Field label="Target date" required>
                    <input
                        type="date"
                        min={minDate}
                        data-testid="host-target-date"
                        value={form.target_date}
                        onChange={(e) => set("target_date", e.target.value)}
                        className={inputCls}
                    />
                </Field>
            </div>
            <p className="text-[11px] text-ink-soft">
                Must be at least 2 weeks out — so we have time to help fill the room.
            </p>
        </div>
    );
}

function Step3({ form, set }) {
    const rows = [
        ["Full name", form.full_name],
        ["Email", form.email],
        ["LinkedIn / X", form.social_url],
        ["Organised before", form.organised_before ? `Yes — ${form.organised_details}` : "No"],
        ["Emergent connection", form.emergent_connection],
        ["Event", `${form.event_name} · ${form.event_type}`],
        ["Idea", form.event_idea],
        ["Location", form.event_location],
        ["Venue", form.venue_status],
        ["Attendees", form.expected_attendees],
        ["Target date", form.target_date],
    ];
    return (
        <div className="space-y-4">
            <div className="border border-line rounded-xl divide-y divide-line">
                {rows.map(([k, v]) => (
                    <div
                        key={k}
                        className="flex items-start gap-4 px-4 py-3"
                        data-testid={`host-review-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    >
                        <div className="w-40 shrink-0 text-[11px] uppercase tracking-[0.18em] text-ink-soft">
                            {k}
                        </div>
                        <div className="text-ink text-sm break-words flex-1">
                            {v || "—"}
                        </div>
                    </div>
                ))}
            </div>
            <label className="flex items-start gap-3 cursor-pointer" data-testid="host-consent-label">
                <input
                    type="checkbox"
                    checked={form.consent}
                    onChange={(e) => set("consent", e.target.checked)}
                    data-testid="host-consent"
                    className="mt-1 accent-[#5fe3c9]"
                />
                <span className="text-sm text-ink-soft leading-relaxed">
                    I agree to be contacted by the Emergent team about this application.
                </span>
            </label>
        </div>
    );
}

function SuccessState({ result, navigate, onClose }) {
    return (
        <div className="text-center py-6" data-testid="host-success">
            <div className="vc-section-label text-[#90FDA6] mb-4">
                Application received
            </div>
            <p className="text-ink text-base leading-relaxed mb-2">
                We review new host events within{" "}
                <span className="text-[#5fe3c9] font-display font-bold">
                    3 business days
                </span>
                .
            </p>
            <p className="text-ink-soft text-sm mb-6">
                Your reference ID is{" "}
                <span className="font-mono text-ink">{result.reference_id}</span>
                . Save it — you'll use it to check status.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                    type="button"
                    onClick={() => {
                        onClose();
                        navigate(`/check/${result.reference_id}`);
                    }}
                    className="vc-btn vc-btn-accent"
                    data-testid="host-success-check"
                >
                    Check status
                </button>
                <button
                    type="button"
                    onClick={onClose}
                    className="vc-btn vc-btn-ghost"
                >
                    Close
                </button>
            </div>
        </div>
    );
}
